import React, { useEffect, useState, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import axios from 'axios'; // Assuming you are using axios for making API calls
import BlockWordModal from './Modals/BlockedWordModal';
import "./ItemGrid.css";
import { BuyingOptionEnum, EbayItemViewModel, BlockedWordModel, getBaseURL } from './ItemGridModel';
import { isTryStatement } from 'typescript';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../Dashboard/Options/store';
import { fetchBlockedSellers } from '../Dashboard/Options/BlockedSellers/blockedSellersSlice';
import FavoriteItemModal from './Modals/FavoriteItemModal';
import { randomBytes, randomInt } from 'crypto';
import { axiosInstance } from '../Auth/axiosInstance';
import { addItems } from './moduls/ItemSlice';
// Define the BuyingOptionEnum and EbayItemViewModel interface


const ItemGrid: React.FC = () => {
    const items = useSelector((state: RootState) => state.itemsTable.items); 
    const [hoveredImage, setHoveredImage] = useState<string | null>(null);
    const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number } | null>(null);
    const [isExecuting, setIsExecuting] = useState<boolean>(false);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const [isBlockedWordModalOpen, setIsBlockedWordModalOpen] = useState(false);
    const [isFavoriteItemdModalOpen, setIsFavoriteItemModalOpen] = useState(false);
    const [selectedTitle, setSelectedTitle] = useState<string>("");
    const [currentPrice, setCurrentPrice] = useState<number | null>();
    const [baseUrl, setBaseUrl] = useState<string>("");
    const dispatch = useDispatch<AppDispatch>();
    const connectionRef = useRef<signalR.HubConnection | null>(null);
    const [onlyAuction, setOnlyAuction] = useState(false);
    const [onlyFavorite, setOnlyFavorite] = useState(false);

    useEffect(() => {
        const setupSignalRConnection = async () => {
            // Prevent duplicate connection setup

            try {
                const url = await getBaseURL();
                setBaseUrl(url); // Set the base URL in state

                const connection = new signalR.HubConnectionBuilder()
                    .withUrl(`${url}hub`, {
                        transport: signalR.HttpTransportType.WebSockets,
                    })
                    .configureLogging(signalR.LogLevel.Information)
                    .build();

                if (connectionRef.current) return;

                connectionRef.current = connection;

                connection.on("ReceiveMessage", (item: string) => {
                    const itemObject: EbayItemViewModel = JSON.parse(item);
                    dispatch(addItems([itemObject]));
                });

                connection.onreconnected(() => {
                    console.log("Reconnected to SignalR hub");
                });

                connection.onclose((error) => {
                    if (error) {
                        console.error("Connection closed with error: ", error);
                    } else {
                        console.log("Connection to SignalR hub closed gracefully");
                    }
                    setTimeout(() => {
                        console.log("Trying reconect SignalR")
                        setupSignalRConnection();
                    }, 3000);
                });

                try {
                    await connection.start();
                    console.log("Connected to SignalR hub");
                } catch (err) {
                    console.error("Connection failed: ", err);
                }
            } catch (error) {
                console.error("Error setting up SignalR connection:", error);
            }
        };

        const setupExecutionState = async()=>{
            const IsExecute = await axiosInstance.get<boolean>("scheduler/start-stop-state");
            setIsExecuting(IsExecute.data);
        }

        setupSignalRConnection();
        setupExecutionState();
        // Cleanup when component unmounts
        return () => {
            if (connectionRef.current) {
                connectionRef.current.stop();
                console.log("SignalR connection stopped");
                connectionRef.current = null; // Clear the ref
            }
        };
    }, []);

    // Sorting items, putting favourite items first
    const sortedItems = items
    .filter((item) => (onlyAuction ? item.BuyingOptions.includes(BuyingOptionEnum.AUCTION) : true))
    .filter((item) => (onlyFavorite ? item.IsFavouriteItem : true))
    .sort((a, b) =>  1);

    const handleMouseOver = (imageUrl: string, event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
        setHoveredImage(imageUrl);
        setHoverPosition({ x: event.pageX, y: event.pageY });
    };

    const handleMouseOut = () => {
        setHoveredImage(null);
        setHoverPosition(null);
    };

    const formatDate = (date: Date) => {
        return new Date(date).toLocaleTimeString();
    };

    const ItemsCount = () => {
        return  items.length;
    };
    const favoriteItemsCount = () => {
        return items.filter(i=>i.IsFavouriteItem).length;;
    };

    const renderBuyingOptions = (buyingOptions: BuyingOptionEnum[]) => {
        const icons = [];
        if (buyingOptions.includes(BuyingOptionEnum.FIXED_PRICE)) {
            icons.push("💰"); // "Buy It Now" icon
        }
        if (buyingOptions.includes(BuyingOptionEnum.AUCTION)) {
            icons.push("⚡"); // Auction icon
        }
        if (buyingOptions.includes(BuyingOptionEnum.BEST_OFFER)) {
            icons.push("📝"); // Best Offer icon
        }
        return icons.join(" ");
    };

    const handleToggleExecution = async () => {
        try {
            if (isExecuting) {
                await axiosInstance.post('scheduler/start-stop',
                    false,
                    {
                        headers: {
                            'Content-Type': 'application/json' // Set content type
                        }
                    }
                ); // Stop execution
                console.log("Stopped execution");
            } else {
                await axiosInstance.post('scheduler/start-stop',
                    true,
                    {
                        headers: {
                            'Content-Type': 'application/json' // Set content type
                        }
                    }
                ); // Start execution
                console.log("Started execution");
            }
            setIsExecuting(prev => !prev); // Toggle the execution state
        } catch (err) {
            console.error("Error toggling execution:", err);
        }
    };

    const handleBlockWord = (item: EbayItemViewModel): void => {
        setSelectedTitle(item.Title);
        setIsBlockedWordModalOpen(true);
    };

    const handleBlockTitle = async (item: EbayItemViewModel): Promise<void> => {
        // Define the blockTitles array
        const blockTitles: BlockedWordModel[] = [
            {
                text: item.Title, // Assuming item.title contains the title to block
                isTitle: true, // Marking as title
            },
        ];
        try {
            // Make the POST request asynchronously
            await axiosInstance.post("filters/add-blocked-words", {
                blockedWords: blockTitles,
            });
            // Handle success
            alert(`Blocked titles added successfully:" ${blockTitles}`);
        } catch (error) {
            // Handle errors
            console.error("Error blocking titles:", error);
        }
    };

    const handleBlockSeller = async (item: EbayItemViewModel): Promise<void> => {
        // Define the blockTitles array
        const blockSellerName: string = item.Seller.SellerUserName;
        try {
            // Make the POST request asynchronously
            await axiosInstance.post("filters/add-blocked-seller", blockSellerName);
            // Handle success
            dispatch(fetchBlockedSellers());
            alert(`Blocked Seller added successfully: ${blockSellerName}`);
        } catch (error) {
            // Handle errors
            console.error("Error blocking titles:", error);
        }
    };

    const handleAddFavoriteItem = async (item: EbayItemViewModel): Promise<void> => {
        // Define the blockTitles array
        setSelectedTitle(item.Title);
        setCurrentPrice(item.CurrentPrice);
        setIsFavoriteItemModalOpen(true);
    };

    useEffect(() => {
        const closeDropdown = (event: MouseEvent) => {
            const dropdowns = document.querySelectorAll(".dropdown-menu.show");
            dropdowns.forEach((dropdown) => {
                // Check if the clicked element is not inside the dropdown or button
                const button = dropdown.previousElementSibling; // The button that toggles the dropdown
                if (
                    !dropdown.contains(event.target as Node) &&
                    !button?.contains(event.target as Node)
                ) {
                    dropdown.classList.remove("show");
                }
            });
        };

        document.addEventListener("click", closeDropdown);
        return () => document.removeEventListener("click", closeDropdown);
    }, []);

    return (
        <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
            <div className='table-header'>
            <h1 className='table-title'>Items Table</h1>
            {/* Toggle Start and Stop button */}
            <div className="controls">
              <div className='left-controls'>
                {/* Auction Switch */}
                <label className="switch">
                    <input
                        type="checkbox"
                        checked={onlyAuction}
                        onChange={(e) => setOnlyAuction(e.target.checked)}
                    />
                    <span className="slider"></span>
                    <span className="label-text">Only Auction</span>
                </label>
                {/* Favorite Item Switch */}
                <label className="switch">
                    <input
                        type="checkbox"
                        checked={onlyFavorite}
                        onChange={(e) => setOnlyFavorite(e.target.checked)}
                    />
                    <span className="slider"></span>
                    <span className="label-text">Favorite Items</span>
                </label>
                </div>
                <button
                    onClick={handleToggleExecution}
                    className={`toggle-button ${isExecuting ? 'stop' : 'start'}`}
                >
                    {isExecuting ? 'Stop' : 'Start'}
                </button>
               
                <div className="right-controls">
                    <div className="count-label">
                        <span>Total Items: {ItemsCount()}</span>
                    </div>
                    <div className="count-label">
                        <span>Favorite Items: {favoriteItemsCount()}</span>
                    </div>
                </div>
            </div>
            </div>
         
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                <thead>
                    <tr style={{ backgroundColor: '#f2f2f2', textAlign: 'left' }}>
                        <th style={{ padding: '10px' }}>Image</th>
                        <th style={{ padding: '10px' }}>Title</th>
                        <th style={{ padding: '10px' }}>Price</th>
                        <th style={{ padding: '10px' }}>Shipping Price</th>
                        <th style={{ padding: '10px' }}>Total Price</th>
                        <th style={{ padding: '10px' }}>Condition</th>
                        <th style={{ padding: '10px' }}>Country</th>
                        <th style={{ padding: '10px' }}>Seller</th>
                        <th style={{ padding: '10px' }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedItems.map(item => (
                        <tr
                            key={item.ID}
                            style={{
                                borderBottom: '1px solid #ddd',
                                backgroundColor: item.IsFavouriteItem ? '#fff3cd' : 'inherit' // Highlight if IsFavouriteItem is true
                            }}
                        >
                            <td style={{ padding: '10px' }}>
                                {item.ImageUrl ? (
                                    <img
                                        src={item.ImageUrl}
                                        alt={item.Title}
                                        style={{ width: '50px', cursor: 'pointer', borderRadius: '5px' }}
                                        onMouseOver={(e) => handleMouseOver(item.ImageUrl!, e)}
                                        onMouseOut={handleMouseOut}
                                        ref={imageRef}
                                    />
                                ) : (
                                    "No Image"
                                )}
                            </td>
                            <td style={{ padding: '10px' }}>
                                <a href={item.ItemWebUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#007bff' }}>
                                    {item.Title}
                                </a>
                                <br />
                                <small style={{ color: '#555' }}>Created: {formatDate(item.ItemCreationDate)}-BackRecived:{formatDate(item.BackEndReciveTime)}-FrontRec:{formatDate(new Date())}</small>
                                <br />
                                <span>{renderBuyingOptions(item.BuyingOptions)}</span>
                            </td>
                            <td style={{ padding: '10px' }}>{item.CurrentPrice} {item.Currency}</td>
                            <td style={{ padding: '8px' }}>{item.ShippingPrice ?? '-'} {item.Currency}</td>
                            <td style={{ padding: '8px' }}>{item.TotalPrice} {item.Currency}</td>
                            <td style={{ padding: '10px' }}>{item.ConditionDisplayName}</td>
                            <td style={{ padding: '10px' }}>{item.Location || "Unknown"}</td>
                            <td style={{ padding: '10px' }}>
                                {item.Seller?.SellerUserName} <br />
                                <span style={{ fontSize: '12px', color: '#555' }}>
                                    ({item.Seller?.SellerRaiting} ratings, {item.Seller?.SellerPositiveRaitingPercent}% positive)
                                </span>
                            </td>
                            <td className="dropdown-cell">
                                <div className="dropdown-container">
                                    <button
                                        className="dropdown-button"
                                        onClick={(e) => {
                                            const dropdown = e.currentTarget.nextElementSibling;
                                            dropdown?.classList.toggle("show");
                                        }}
                                    >
                                        Actions
                                    </button>
                                    <ul className="dropdown-menu">
                                        <li>
                                            <button className="dropdown-item" onClick={() => handleBlockWord(item)}>
                                                Block Word
                                            </button>
                                        </li>
                                        <li>
                                            <button className="dropdown-item" onClick={() => handleBlockTitle(item)}>
                                                Block Title
                                            </button>
                                        </li>
                                        <li>
                                            <button className="dropdown-item" onClick={() => handleBlockSeller(item)}>
                                                Block Seller
                                            </button>
                                        </li>
                                        <li>
                                            <button className="dropdown-item" onClick={() => handleAddFavoriteItem(item)}>
                                                Add Favorite Item
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </td>

                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Enlarged image on hover */}
            {hoveredImage && hoverPosition && (
                <div
                    style={{
                        position: 'absolute',
                        top: `${hoverPosition.y + 20}px`,
                        left: `${hoverPosition.x + 20}px`,
                        border: '1px solid #ccc',
                        padding: '5px',
                        backgroundColor: '#fff',
                        borderRadius: '10px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000
                    }}
                >
                    <img src={hoveredImage} alt="Hovered item" style={{ width: '300px', borderRadius: '10px' }} />
                </div>
            )}
            {isBlockedWordModalOpen && (
                <BlockWordModal
                    title={selectedTitle}
                    isOpen={isBlockedWordModalOpen}
                    onClose={() => setIsBlockedWordModalOpen(false)}
                />
            )}
            {isFavoriteItemdModalOpen && (
                <FavoriteItemModal
                    title={selectedTitle}
                    currentPrice={currentPrice ?? 0}
                    isOpen={isFavoriteItemdModalOpen}
                    onClose={() => setIsFavoriteItemModalOpen(false)}
                />
            )}
        </div>
    );
};

export default ItemGrid;
